import { TrackerDiv } from "@/components/Tracker";
import { LOCAL_STORAGE_KEY } from "@/constants/localStorageKey";
import { SearchOutlined } from "@ant-design/icons";
import { useLocalStorageState } from "ahooks";
import { Button, Input } from "antd";
import classNames from "classnames";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import HBIcon from "../../components/HBIcon";
import { isEnter } from "../../utils/keyBoard";
import styles from "./search.module.less";

/**
 * 花瓣内容搜索输入框
 * @param {Omit<import("antd").InputProps, "placeholder"> & {
 *  hideSearchBtn?: boolean,
 *  showSearchSimilarBtn?: boolean,
 *  onSearch?: function,
 *  onSearchSimilarImg?: function,
 *  placeholderCenter?: boolean,
 *  preTags?: Array,
 *  allowClear?: boolean,
 *  onClear?: function,
 *  placeholder?: string|string[],
 *  placeholderPrefix?: string,
 *  isNewLayout?: boolean,
 * }} props
 * @returns {JSX.Element}
 */
function HBSearch(
  {
    hideSearchBtn = false,
    showSearchSimilarBtn = false,
    onSearch,
    onSearchSimilarImg,
    placeholderCenter = false,
    preTags = [],
    allowClear = true,
    onClear,
    placeholderPrefix = "",
    isNewLayout = false,
    ...props
  } = {},
  ref
) {
  const [isFocus, setFocus] = useState(false);

  const [isFilled, setFilled] = useState(false);

  // 当前拼音输入的参数
  const [pinyininput, setPinyin] = useState(false);

  const inputRef = useRef(null);
  const searchBtnRef = useRef(null);
  const similarBtnRef = useRef(null);

  const [redTip, setRedTip] = useLocalStorageState(
    LOCAL_STORAGE_KEY.HB_SEARCH_SIMILAR_BUTTON,
    {
      defaultValue: true,
    }
  );

  useImperativeHandle(
    ref,
    () => ({
      input: inputRef.current.input,
      getCurrentPlaceholder: () => {
        return {
          value: Array.isArray(props.placeholder)
            ? props.placeholder[placeholderIndex.current]
            : props.placeholder,
          index: placeholderIndex.current,
        };
      },
    }),
    [props.placeholder]
  );

  useEffect(() => {
    if (props.value) {
      setFilled(true);
    } else {
      setFilled(false);
    }
  }, [props.value]);

  const tags = useMemo(() => {
    return preTags.filter((tag) => tag.label);
  }, [preTags]);

  const preTagClick = useCallback(() => {
    inputRef.current?.input?.focus();
  }, []);

  const onSearchSimilar = useCallback(() => {
    if (redTip) {
      setRedTip(false);
    }
    inputRef.current.input.blur();
    setFocus(false);
    onSearchSimilarImg && onSearchSimilarImg();
  }, [onSearchSimilarImg, redTip, setRedTip]);

  const tagsElement = tags.length ? (
    <div className={styles.preTags}>
      {tags.map((tag) => {
        if (tag) {
          return (
            <span key={tag.label} className={styles.tag} onClick={preTagClick}>
              {tag.label}
              <HBIcon
                type="ic_close"
                onClick={() => {
                  tag.clear(null);
                }}
              />
            </span>
          );
        }
      })}
    </div>
  ) : null;

  const placeholderRef = useRef();
  const placeholderElement = (
    <div
      className={classNames(styles.placeholder, {
        [styles.center]: placeholderCenter,
      })}
      ref={placeholderRef}
    >
      {Array.isArray(props.placeholder) ? (
        props.placeholder.map((item, index) => {
          return (
            <div className={styles.item} key={index}>
              {placeholderPrefix + item}
            </div>
          );
        })
      ) : (
        <div className={styles.item}>{props.placeholder || "搜索"}</div>
      )}
    </div>
  );

  const searchIcon = isNewLayout ? (
    <HBIcon type="hb_search" className={styles.prefixIcon} />
  ) : (
    <SearchOutlined className={styles.prefixIcon} />
  );

  const prefix = (
    <>
      {tags?.length ? tagsElement : searchIcon}
      {placeholderElement}
    </>
  );

  const suffix =
    hideSearchBtn &&
    !allowClear &&
    !tags.length &&
    !showSearchSimilarBtn ? null : (
      <>
        {((isFilled || tags?.length) && allowClear && (
          <span className={styles.clear}>
            {isNewLayout ? (
              <HBIcon
                className={styles.clearIcon}
                type="ic_clear"
                onClick={() => {
                  onClear && onClear();
                }}
              />
            ) : (
              <HBIcon
                type="ic_close"
                onClick={() => {
                  onClear && onClear();
                }}
              />
            )}
          </span>
        )) ||
          null}

        {showSearchSimilarBtn ? (
          <span
            ref={similarBtnRef}
            className={styles.camera}
            data-gd-click="button_click"
            data-gd-expose="button_expose"
            data-button-name="以图搜索按钮"
            onClick={onSearchSimilar}
            data-tooltip="搜相似图片"
            data-tooltip-position="bottom"
            tabIndex="0"
          >
            <HBIcon type="camera" />
            {redTip ? (
              <img
                className={styles.icon}
                width={21}
                height={11}
                src="//cdn.dancf.com/fe-assets/20231219/09b144a9d096d6b2cc9c37c1bb2a9466.png"
                alt="new"
              />
            ) : null}
          </span>
        ) : null}

        {!hideSearchBtn && (
          <Button
            ref={searchBtnRef}
            className={styles.searchBtn}
            type="primary"
            onClick={(e) => {
              inputRef.current.input.blur();
              onSearch && onSearch(inputRef.current.input.value);
              e.stopPropagation();
            }}
          >
            <SearchOutlined style={{ fontSize: "20px" }} />
          </Button>
        )}
      </>
    );

  // 当前轮播占位词索引
  const placeholderIndex = useRef(0);
  // 轮播占位词
  useEffect(() => {
    const placeholder = props.placeholder;
    let timer;
    function loop() {
      timer = setTimeout(() => {
        const height = placeholderRef.current?.getBoundingClientRect().height;
        placeholderIndex.current++;
        if (placeholderIndex.current >= placeholder.length) {
          placeholderIndex.current = 0;
        }
        placeholderRef.current?.scrollTo({
          top: height * placeholderIndex.current,
          behavior: "smooth",
        });

        loop();
      }, 5000);
    }
    if (
      placeholder &&
      Array.isArray(placeholder) &&
      placeholder.length > 1 &&
      !props.value
    ) {
      loop();
      return () => {
        clearTimeout(timer);
      };
    }
  }, [props.placeholder, props.value]);

  return (
    <TrackerDiv
      className={classNames(styles.wrap, {
        [styles.filled]: isFilled,
        [styles.focus]: isFocus,
        [styles.newVersion]: isNewLayout,
      })}
      style={props.style || {}}
      data-gd-click="button_click"
      data-button-name="搜索框"
      data-subject-name="按钮"
    >
      <Input
        ref={inputRef}
        prefix={prefix}
        suffix={suffix}
        {...props}
        placeholder=""
        className={classNames(styles.search, {
          [styles.hasSearchBtn]: !hideSearchBtn,
        })}
        autoComplete="off"
        onFocus={(e) => {
          setFocus(true);
          props.onFocus && props.onFocus(e);
        }}
        onBlur={(e) => {
          // 搜索按钮点击时，且输入框空内容时，避免按钮点击失效
          if (e.relatedTarget === searchBtnRef.current && !e.target.value) {
            inputRef.current.input.focus();
            return;
          }

          if (e.relatedTarget === similarBtnRef.current) {
            // 避免以图搜图按钮点击失效
            inputRef.current.input.focus();
            return;
          }
          setFocus(false);
          if (e.target.value) {
            setFilled(true);
          } else {
            setFilled(false);
          }
          props.onBlur && props.onBlur(e);
        }}
        onKeyDown={(e) => {
          if (
            isEnter(e) &&
            !pinyininput &&
            // 360浏览器下搜狗中文输入法下，onCompositionStart不会触发，key为Process，所以需要在这里判断
            e.key !== "Process"
          ) {
            onSearch && onSearch(e.target.value);
            e.target.blur();
          }
          props.onKeyDown && props.onKeyDown(e);
        }}
        onInput={(e) => {
          if (e.target.value) {
            setFilled(true);
          } else {
            setFilled(false);
          }
          props.onInput && props.onInput(e);
        }}
        onCompositionStart={() => setPinyin(true)}
        onCompositionEnd={() =>
          // 没有异步在火狐浏览器里无法输入中文
          setTimeout(() => {
            setPinyin(false);
          }, 1)
        }
      />
    </TrackerDiv>
  );
}

export default forwardRef(HBSearch);
